/**
 * Kind classification + panel grouping for pipeline automation rules (SE-5506).
 *
 * The schema has no `kind` field — every rule is a trigger + action pair. The
 * "Automations" vs "Rules/Guards" split the panel renders is DERIVED from that
 * shape: a rule that fires on stage entry and routes the card to the review
 * queue is the on-schema representation of a condition-gated stage entry (a
 * Guard); everything else is an Automation.
 *
 * This is the single seam that decides the family, so the builder's Kind toggle
 * and the panel grouping stay in lock-step: a Guard authored by the builder
 * (CARD_ENTERED_STAGE + ADD_TO_REVIEW_QUEUE) reads back as a Guard here.
 * @module features/player-kanban/utils/automationRuleKind
 */

import {
  PipelineAutomationActionType,
  type PipelineAutomationRuleFragmentFragment,
  PipelineAutomationTriggerType,
} from "@/generated/graphql";

import type { AutomationRuleKind } from "../types/automationRules";

/** Minimal shape needed to classify a rule (trigger + action only). */
export interface ClassifiableRule {
  readonly triggerType: PipelineAutomationTriggerType;
  readonly actionType: PipelineAutomationActionType;
}

/**
 * Classify a rule into its UI family.
 *
 * Guard ⇔ fires on stage entry AND routes to the review queue — the on-schema
 * encoding of a condition-gated stage entry. All other shapes are Automations.
 * @param rule - The rule (or in-progress form) to classify.
 * @returns `"guard"` or `"automation"`.
 */
export const classifyAutomationRuleKind = (
  rule: ClassifiableRule
): AutomationRuleKind =>
  rule.triggerType === PipelineAutomationTriggerType.CardEnteredStage &&
  rule.actionType === PipelineAutomationActionType.AddToReviewQueue
    ? "guard"
    : "automation";

/** A rules list partitioned into its two families, each preserving input order. */
export interface GroupedAutomationRules {
  /** Free trigger + action rules. */
  readonly automations: readonly PipelineAutomationRuleFragmentFragment[];
  /** Condition-gated stage-entry guards. */
  readonly guards: readonly PipelineAutomationRuleFragmentFragment[];
}

/**
 * Partition a flat rules list into Automations and Guards for the panel's two
 * grouped sections. Order within each group is preserved from the input.
 * @param rules - The rules to group.
 * @returns The {@link GroupedAutomationRules}.
 */
export const groupAutomationRules = (
  rules: readonly PipelineAutomationRuleFragmentFragment[]
): GroupedAutomationRules => ({
  automations: rules.filter(
    rule => classifyAutomationRuleKind(rule) === "automation"
  ),
  guards: rules.filter(rule => classifyAutomationRuleKind(rule) === "guard"),
});
